/**
 * Operation Results Component
 * 
 * Displays results of upload, download, or delete operations.
 * Shows success/failure counts, per-route status, and detailed information.
 * 
 * @module OperationResults
 */

import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  DirectionsBusOutlined as ShiftIcon,
  CheckCircleOutlined as ShiftCreatedIcon,
  WarningAmberOutlined as ShiftWarningIcon,
  ErrorOutlineOutlined as ShiftErrorIcon,
} from '@mui/icons-material';

import { Icon } from '@liftango/ui-icons';
/**
 * Per-shift outcome within a single route upload, derived from the route's
 * assigned operations (`operationAssignments`). One entry per distinct shift
 * solution that the upload attempted via the stubbed `createshiftsolution` path.
 */
export interface RouteShiftResult {
  /** Human label for the shift solution (operation × days). */
  label: string;
  /** Whether creation succeeded (true on a live UAT write; false when gated/skipped/failed). */
  created: boolean;
  /** Ops shift-solution id returned by a successful live `createshiftsolution` write. */
  shiftSolutionId?: string;
  /** Non-fatal warning (e.g. gated PROD, or a missing operation/vehicle that skipped the write). */
  warning?: string;
  /**
   * The shift was skipped by the pre-upload overlap check because its vehicle is
   * already booked for overlapping dates (pre-existing in Ops, or created earlier
   * in this same upload). No write was attempted. `warning` carries the reason.
   */
  skipped?: boolean;
  /** Fatal error for this specific shift solution, if any. */
  error?: string;
}

/**
 * Result for a single route operation
 */
export interface RouteOperationResult {
  /** Route ID */
  routeId: string;
  /** Route name */
  routeName: string;
  /** Whether operation succeeded */
  success: boolean;
  /** Error message (if failed) */
  error?: string;
  /** Created route version ID (for uploads) */
  createdRouteVersionId?: string;
  /** Human-readable version name (for uploads) */
  routeVersionName?: string;
  /** UAT route ID (for uploads) */
  uatRouteId?: string;
  /** Whether this upload bumped an existing route to a new version (vs creating fresh) */
  wasUpdate?: boolean;
  /** Whether the new version was applied to future shifts */
  shiftUpdateApplied?: boolean;
  /** Error from the shift-application follow-up step, if any */
  shiftUpdateError?: string;
  /** Whether dated shift-solution creation was attempted */
  datedShiftAttempted?: boolean;
  /** Whether dated shift-solution creation succeeded */
  datedShiftCreated?: boolean;
  /** Created dated shift-solution ID */
  datedShiftSolutionId?: string;
  /** Warning from dated shift-solution orchestration */
  datedShiftWarning?: string;
  /** Error from dated shift-solution orchestration */
  datedShiftError?: string;
  /**
   * Per-shift breakdown when the upload derived shift solutions from the route's
   * assigned operations (`operationAssignments`). Optional / backward compatible:
   * absent for routes uploaded via the legacy single-intent path.
   */
  shiftResults?: RouteShiftResult[];
  /** Additional non-fatal warnings surfaced for the route */
  warnings?: string[];
  /** Additional information */
  info?: string;
}

/**
 * Props for OperationResults component
 */
export interface OperationResultsProps {
  /** Operation type */
  operationType: 'upload' | 'download' | 'delete';
  /** Array of route operation results */
  results: RouteOperationResult[];
  /** Whether results are dismissible */
  dismissible?: boolean;
  /** Callback when results are dismissed */
  onDismiss?: () => void;
  /** Whether to show detailed results by default */
  defaultExpanded?: boolean;
}

/**
 * OperationResults Component
 * 
 * Displays operation results with success/failure counts and per-route details.
 */
export const OperationResults: React.FC<OperationResultsProps> = ({
  operationType,
  results,
  dismissible = true,
  onDismiss,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || results.length === 0) {
    return null;
  }

  const successCount = results.filter((r) => r.success).length;
  const errorCount = results.filter((r) => !r.success).length;
  const updatedCount = results.filter((r) => r.success && r.wasUpdate).length;
  const createdCount = successCount - updatedCount;
  const totalCount = results.length;

  const severity: 'success' | 'error' | 'warning' | 'info' =
    errorCount === 0 ? 'success' : successCount === 0 ? 'error' : 'warning';

  const operationLabel =
    operationType === 'upload' ? 'Upload' : operationType === 'download' ? 'Download' : 'Delete';

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity={severity}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              aria-label="expand details"
              size="small"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <Icon size={20} name="chevron-up" /> : <Icon size={20} name="chevron-down" />}
            </IconButton>
            {dismissible && (
              <IconButton aria-label="dismiss" size="small" onClick={handleDismiss}>
                <Icon size={20} name="xmark" />
              </IconButton>
            )}
          </Box>
        }
      >
        <AlertTitle>
          {operationLabel} {errorCount === 0 ? 'Completed Successfully' : 'Completed with Errors'}
        </AlertTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <Typography variant="body2">
            {successCount} of {totalCount} route{totalCount === 1 ? '' : 's'} succeeded
          </Typography>
          {operationType === 'upload' && createdCount > 0 && (
            <Chip
              icon={<Icon size={20} name="circle-check" />}
              label={`${createdCount} created`}
              color="success"
              size="small"
            />
          )}
          {operationType === 'upload' && updatedCount > 0 && (
            <Chip
              icon={<Icon size={20} name="circle-check" />}
              label={`${updatedCount} updated`}
              color="info"
              size="small"
            />
          )}
          {operationType !== 'upload' && successCount > 0 && (
            <Chip
              icon={<Icon size={20} name="circle-check" />}
              label={`${successCount} success${successCount === 1 ? '' : 'es'}`}
              color="success"
              size="small"
            />
          )}
          {errorCount > 0 && (
            <Chip
              icon={<Icon size={20} name="circle-exclamation" />}
              label={`${errorCount} error${errorCount === 1 ? '' : 's'}`}
              color="error"
              size="small"
            />
          )}
        </Box>

        {/* Expandable details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Detailed Results:
            </Typography>
            <List dense>
              {results.map((result, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {result.success ? (
                      <Icon size={20} name="circle-check" />
                    ) : (
                      <Icon size={20} name="circle-exclamation" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={result.routeName}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box>
                        {result.success ? (
                          <Typography variant="caption" color="success.main">
                            {operationType === 'upload' && result.routeVersionName
                              ? `Version: ${result.routeVersionName}`
                              : operationType === 'upload' && result.createdRouteVersionId
                              ? `Created version: ${result.createdRouteVersionId}`
                              : operationType === 'upload' && result.uatRouteId
                              ? `Route ID: ${result.uatRouteId}`
                              : 'Success'}
                            {operationType === 'upload' && result.shiftUpdateApplied && (
                              <> • Applied to future shifts</>
                            )}
                            {operationType === 'upload' && result.shiftUpdateError && (
                              <> • Future-shift update failed: {result.shiftUpdateError}</>
                            )}
                            {operationType === 'upload' && result.datedShiftCreated && (
                              <> • Dated shift solution created{result.datedShiftSolutionId ? ` (${result.datedShiftSolutionId})` : ''}</>
                            )}
                            {operationType === 'upload' && result.datedShiftWarning && (
                              <> • Dated shift warning: {result.datedShiftWarning}</>
                            )}
                            {operationType === 'upload' && result.datedShiftError && (
                              <> • Dated shift failed: {result.datedShiftError}</>
                            )}
                            {operationType === 'upload' &&
                              result.warnings &&
                              result.warnings.map((warning, warningIndex) => (
                                <React.Fragment key={`warning-${warningIndex}`}>
                                  {' • '}
                                  {warning}
                                </React.Fragment>
                              ))}
                            {result.info && ` - ${result.info}`}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="error">
                            {result.error || 'Unknown error'}
                          </Typography>
                        )}
                        {/* Per-shift breakdown derived from the route's assigned
                            operations. Backward compatible: only renders when the
                            upload populated `shiftResults`. */}
                        {operationType === 'upload' &&
                          result.shiftResults &&
                          result.shiftResults.length > 0 && (
                            <List dense disablePadding sx={{ mt: 0.5, ml: 1 }}>
                              {result.shiftResults.map((shift, shiftIndex) => (
                                <ListItem key={`shift-${shiftIndex}`} disableGutters sx={{ py: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    {shift.error ? (
                                      <ShiftErrorIcon color="error" fontSize="small" />
                                    ) : shift.created ? (
                                      <ShiftCreatedIcon color="success" fontSize="small" />
                                    ) : shift.warning ? (
                                      <ShiftWarningIcon color="warning" fontSize="small" />
                                    ) : (
                                      <ShiftIcon color="action" fontSize="small" />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    primaryTypographyProps={{ variant: 'caption' }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                    primary={shift.label}
                                    secondary={
                                      shift.error
                                        ? shift.error
                                        : shift.created
                                        ? 'Shift solution created'
                                        : shift.warning
                                        ? shift.warning
                                        : undefined
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Collapse>
      </Alert>
    </Box>
  );
};
